import { forwardRef, useMemo } from 'react'
import { TransitionProps } from '@mui/material/transitions'
import { TRANSITION_DURATION, Z_INDEX } from '../constants'
import { MAX_HEIGHT, paperStyles, contentStyles } from './modal.styles'
import { Theme, alpha } from '@mui/material/styles'
import { SystemStyleObject } from '@mui/system'
import Dialog, { DialogProps } from '@mui/material/Dialog'
import Paper, { PaperProps } from '@mui/material/Paper'
import Draggable from './Draggable'
import Overflow from './Overflow'
import Grow from '@mui/material/Grow'
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import { LuX } from 'react-icons/lu'

interface Props extends DialogProps {
  icon?: React.ReactNode
  color?: keyof Theme['palette'] | string
  inline?: boolean
  disableOverflow?: boolean
  // Hide the close button. Use this only when the popup has its own close control in the same place.
  hideClose?: boolean
  position?: { x: number; y: number }
  onPositionChange?: (position: { x: number; y: number }) => void
}

export default function Modal({
  icon,
  color,
  children,
  sx,
  inline,
  maxWidth,
  onClose,
  open = true,
  disableOverflow,
  hideClose,
  position,
  onPositionChange,
  ...rest
}: Props) {
  const DraggablePaper = useMemo(
    () => (paperProps: PaperProps) =>
      // elevation is consumed by the DialogPaper layer above, so pin it on the inner Paper too
      <Draggable {...paperProps} elevation={0} position={position} onPositionChange={onPositionChange} />,
    [position, onPositionChange]
  )
  const showClose = !!onClose && !hideClose
  const content = (
    <>
      {icon && (
        <Box
          sx={{
            left: -10,
            top: 40,
            position: 'absolute',
            display: { xs: 'none', sm: 'block' }
          }}>
          {icon}
        </Box>
      )}
      <Box
        sx={{
          ...contentStyles(color),
          position: 'relative',
          ...sx,
        }}
      >
        {/* ⚠ EVERY POPUP THAT CAN CLOSE SHOWS THIS BUTTON. ESC also closes it, but a touch screen
            has no ESC, and most popups have no Cancel button. Without this button, a user who
            opened a popup by accident could not close it (New Organization, 2026-09-24).
            A popup that must not close gives no onClose, and then this button is not shown. */}
        {showClose && (
          <Tooltip title="Close" placement="top" arrow>
            <IconButton
              size="small"
              aria-label="Close"
              onClick={() => onClose({}, 'backdropClick')}
              sx={theme => {
                const accent = accentColor(theme, color)
                return {
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  zIndex: 1,
                  width: 28,
                  height: 28,
                  padding: 0,
                  // A circle. The theme gives IconButton a rounded square.
                  borderRadius: '50%',
                  color: accent,
                  backgroundColor: alpha(accent, 0.14),
                  '&:hover': { backgroundColor: alpha(accent, 0.24) },
                  '& svg': { strokeWidth: 2.5 },
                }
              }}
            >
              <LuX size={15} />
            </IconButton>
          </Tooltip>
        )}
        {disableOverflow ? (
          children
        ) : (
          <Overflow
            sx={{
              maxHeight: MAX_HEIGHT,
              paddingX: { xs: 0, sm: 2 },
              paddingY: { xs: 0, sm: 2 },
              // Below sm the content has no padding, so the header row would run under the close
              // button (the Members rename field, for one). Keep a strip for the button there.
              // ⚠ The strip goes HERE, in the scroll area, not on the outer box: the outer box has
              // the same maxHeight and overflow hidden, so a strip there cut the last 40px of every
              // tall window off at the end of its scroll.
              // sm: 0 adds nothing: at sm and up the inner scroll element keeps its own paddingY.
              ...(showClose && { paddingTop: { xs: 5, sm: 0 } }),
            }}
          >
            {children}
          </Overflow>
        )}
      </Box>
    </>
  )

  if (inline) {
    // ⚠⚠ AN INLINE DIALOG IS PART OF THE PAGE, SO IT IS NOT A MUI Dialog (app#382). A MUI Dialog
    // is always modal: it put aria-hidden="true" on #root, which hid the whole app FROM A SCREEN
    // READER, this dialog included, and its focus trap kept Tab inside the dialog. Register,
    // AccessRequest and InviteAccept are an inline dialog and nothing else, so a screen reader
    // read nothing on those pages. This is the same paper, sized as the Dialog sized it, with
    // no aria-modal, no focus trap and no aria-hidden on anything.
    return (
      <Grow in appear timeout={TRANSITION_DURATION}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
          <Paper
            role="dialog"
            aria-label={rest['aria-label']}
            aria-labelledby={rest['aria-labelledby']}
            elevation={0}
            onKeyDown={
              onClose
                ? event => {
                    if (event.key !== 'Escape') return
                    event.stopPropagation()
                    onClose(event, 'escapeKeyDown')
                  }
                : undefined
            }
            sx={[inlinePaperStyles(maxWidth), paperStyles]}
          >
            {content}
          </Paper>
        </Box>
      </Grow>
    )
  }

  return (
    <Dialog
      {...(rest as DialogProps)}
      sx={{ zIndex: Z_INDEX.MODAL }}
      open={open}
      fullWidth
      hideBackdrop
      slots={{ transition: Transition }}
      slotProps={{
        paper: {
          // component (not slots.paper) keeps MUI's DialogPaper in the tree, preserving
          // its maxWidth/layout CSS — replicates the removed PaperComponent behavior
          component: DraggablePaper,
          elevation: 0,
          sx: {
            ...paperStyles,
            width: 'calc(100% - 32px) !important',
          },
        },
      }}
      maxWidth={maxWidth}
      onClose={onClose}
    >
      {content}
    </Dialog>
  )
}

// The popup's accent colour, for the close button. `color` is a palette path ('primary.main') or a
// CSS colour. A popup with no colour gets the text grey.
// ⚠ alpha() THROWS on a string that is not a colour ('inherit', 'primary' with no '.main'), and the
// popup then does not render. The types allow those values, so a value alpha() cannot read gets the
// grey too.
const accentColor = (theme: Theme, color?: string): string => {
  const grey = theme.palette.contrast.high
  if (!color) return grey
  const value = color
    .split('.')
    .reduce<unknown>((node, key) => (node as Record<string, unknown> | undefined)?.[key], theme.palette)
  const accent = typeof value === 'string' ? value : color
  try {
    alpha(accent, 0)
    return accent
  } catch {
    return grey
  }
}

// The size MUI's Dialog gave its paper, with fullWidth and the theme's margin 0.
const inlinePaperStyles = (maxWidth: DialogProps['maxWidth'] = 'sm'): SystemStyleObject<Theme> => ({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  overflowY: 'auto',
  outline: 0,
  margin: 0,
  width: 'calc(100% - 64px)',
  maxWidth: ({ breakpoints }) =>
    maxWidth === false
      ? 'calc(100% - 64px)'
      : maxWidth === 'xs'
        ? Math.max(breakpoints.values.xs, 444)
        : maxWidth in breakpoints.values
          ? breakpoints.values[maxWidth as keyof typeof breakpoints.values]
          : maxWidth,
})

const Transition = forwardRef(function Transition(
  props: TransitionProps & { children: React.ReactElement },
  ref: React.Ref<unknown>
) {
  return <Grow ref={ref} {...props} timeout={TRANSITION_DURATION} />
})
